import React, { useState } from "react";
import { T } from "../theme";
import { fmtMoney, taxSuffix, currencyPrefix } from "./money";
import { rosterForMonth, getCurrentMonth } from "./calc";

// Who sits behind an "Included monthly" line: roster entries of the type the
// line declares (`includes[].roster: "leadership" | "team"`), or, when it
// declares none, the type its label names. Nothing → the line doesn't expand.
export function includeNames(cfg, include, month) {
  const type = include.roster || (/leadership/i.test(include.label || "") ? "leadership" : /team/i.test(include.label || "") ? "team" : null);
  if (!type) return [];
  const roster = rosterForMonth(cfg, month || getCurrentMonth());
  const typed = roster.filter(c => c.type);
  if (typed.length === 0) return type === "leadership" ? roster.map(c => c.label) : [];
  return typed.filter(c => c.type === type).map(c => c.label);
}

// `retainer` module — the fixed-monthly overview card: headline block,
// what's included, and the retainer total. If the client also declares a
// `drawdownPool`, its monthly accrual is shown as a line in this card
// (matching the original combined layout).
export default function RetainerCard({ retainer, drawdownPool, cfg }) {
  const includes = retainer.includes || [];
  const [open, setOpen] = useState({});
  return (
    <div style={{ background: T.canvas, borderRadius: T.radiusMd, border: `1px solid ${T.borderSubtle}`, padding: "20px 24px", marginBottom: 24, boxShadow: T.shadowSm }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 16 }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: T.root }}>{retainer.label}</h3>
        <span style={{ fontSize: 18, fontWeight: 700, color: T.root }}>{fmtMoney(retainer.amount)}<span style={{ fontSize: 11, fontWeight: 400, color: T.fgMuted }}>/mo</span></span>
      </div>
      <div style={{ fontSize: 11, color: T.fgMuted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, fontWeight: 600 }}>Included monthly</div>
      {includes.map((fc, i) => {
        const names = cfg ? includeNames(cfg, fc) : [];
        const can = names.length > 0;
        const isOpen = !!open[i];
        return (
          <div key={i} style={{ borderBottom: i < includes.length - 1 ? `1px solid ${T.borderSubtle}` : "none" }}>
            <div onClick={can ? () => setOpen(o => ({ ...o, [i]: !isOpen })) : undefined} role={can ? "button" : undefined} aria-expanded={can ? isOpen : undefined} style={{ padding: "6px 0", display: "flex", gap: 8, alignItems: "baseline", cursor: can ? "pointer" : "default" }}>
              <span style={{ fontSize: 12, color: T.statusOn }}>✓</span>
              <div style={{ flex: 1, minWidth: 0 }}><span style={{ fontSize: 13, fontWeight: 600, color: T.root }}>{fc.label}</span><span style={{ fontSize: 11, color: T.fgMuted, marginLeft: 8 }}>{fc.description}</span></div>
              {can && <span style={{ fontSize: 11, color: T.fgMuted, whiteSpace: "nowrap" }}>{names.length} {isOpen ? "▾" : "▸"}</span>}
            </div>
            {can && isOpen && (
              <div style={{ padding: "0 0 8px 20px", display: "flex", flexWrap: "wrap", gap: "4px 14px" }}>
                {names.map(n => <span key={n} style={{ fontSize: 12, color: T.fgSecondary }}>{n}</span>)}
              </div>
            )}
          </div>
        );
      })}
      {drawdownPool && (
        <div style={{ marginTop: 12, padding: "10px 0 0", borderTop: `1px solid ${T.borderSubtle}`, display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
          <span style={{ fontSize: 12, color: T.fgMuted }}>{drawdownPool.label || "Strategy draw-down"} (accrues monthly, rolls over)</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: T.root }}>+{fmtMoney(drawdownPool.monthlyAccrual)}<span style={{ fontSize: 11, fontWeight: 400, color: T.fgMuted }}>/mo</span></span>
        </div>
      )}
      <div style={{ marginTop: 8, display: "flex", justifyContent: "space-between", alignItems: "baseline", padding: "10px 0 0", borderTop: `1px solid ${T.borderSubtle}` }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: T.root }}>Monthly retainer</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: T.root }}>{currencyPrefix(cfg)}{fmtMoney(retainer.monthlyTotal)}{taxSuffix(cfg)}</span>
      </div>
    </div>
  );
}
